import { isEmpty, isEmail } from '@jjj/components';
import _ from 'lodash';
import actions from './actions';
import { createReducer } from '../../../../helpers/reducer';
import { keyBy } from '../../../../helpers/core';
import { t } from '../../../../i18n';

const initState = {
  loading: false,
  isModalVisible: false,
  step: 1,
  isAssociateSelected: false,
  states: [],
  findAssociateForm: null,
  confirmAssociateForm: null,
  errorMessage: '',
  validationErrors: [],
};

const initialFindAssociateData = {
  pernr: '',
  ssn: '',
};

function formatErrorMessage(validations) {
  const errors = [];
  const errorTypes = {};

  const keys = Object.keys(validations);

  for (let i = 0; i < keys.length; i += 1) {
    const fieldName = keys[i];
    validations[fieldName].forEach(error => {
      const errorType = _.capitalize(error);

      if (!errorTypes[errorType]) {
        errorTypes[errorType] = [];
      }

      errorTypes[errorType].push(_.startCase(fieldName.replace(/_/g, ' ')));
    });
  }

  const types = Object.keys(errorTypes);

  for (let i = 0; i < types.length; i += 1) {
    const errorType = types[i];
    errors.push(`${errorType}: ${errorTypes[errorType].join(', ')}`);
  }

  return errors;
}

function validate(attr, value) {
  let error = '';

  if (attr === 'pernr') {
    if (isEmpty(value)) {
      error = t('dash.validation.required_error', { fieldName: t('dash.applicants.pernr_label') });
    }
  }

  if (attr === 'ssn') {
    if (isEmpty(value)) {
      error = t('dash.validation.required_error', { fieldName: t('dash.common.worker_numerical_identifier') });
    }
  }

  if (attr === 'email') {
    if (isEmpty(value)) {
      error = t('dash.validation.required_error', { fieldName: t('dash.applicants.email_label') });
    } else if (value && !isEmail(value)) {
      error = t('dash.validation.valid_error', { fieldName: t('dash.applicants.email_label') });
    }
  }

  if (attr === 'phone') {
    if (isEmpty(value)) {
      error = t('dash.validation.required_error', { fieldName: t('dash.applicants.phone_label') });
    }
  }

  return error;
}

function onTouch(state, { payload }) {
  const form = JSON.stringify(state[payload.formName]);

  return {
    ...state,
    [payload.formName]: JSON.parse(form.replace(/"touched":false/g, '"touched":true')),
  };
}

function onToggleAssociateSelect(state, { payload }) {
  return {
    ...state,
    isAssociateSelected: payload,
  };
}

function onShowModal(state) {
  return {
    ...state,
    isModalVisible: true,
  };
}

function onCloseModal(state) {
  return {
    ...state,
    isModalVisible: false,
  };
}

function onSetStep(state, { payload }) {
  return {
    ...state,
    step: payload,
  };
}

function onCreateFindAssociateForm(state) {
  const propsArray = Object.keys(initialFindAssociateData);
  const findAssociateForm = keyBy(
    propsArray,
    prop => prop,
    prop => ({
      name: prop,
      value: initialFindAssociateData[prop],
      error: validate(prop, initialFindAssociateData[prop]),
      touched: false,
    }),
  );

  return {
    ...state,
    findAssociateForm,
  };
}

function onChangeProperty(state, { payload }) {
  const { formName, attr, value, index } = payload;

  if (index !== undefined) {
    state[formName][attr].value[index] = value;
  } else {
    state[formName][attr] = {
      value,
      error: validate(attr, value),
      name: attr,
      touched: true,
    };
  }

  return {
    ...state,
    [formName]: {
      ...state[formName],
    },
  };
}

function onLoadAssociateFormDataSuccess(state, { payload }) {
  const { states = [] } = payload;

  return {
    ...state,
    states,
  };
}

function onFindAssociateRequest(state) {
  return {
    ...state,
    loading: true,
  };
}

function onBeginBackgroundCheckRequest(state) {
  return {
    ...state,
    loading: true,
  };
}

function onFindAssociateSuccess(state, { payload }) {
  const { associateData } = payload;

  const associatePropsArray = Object.keys(associateData);
  const confirmAssociateForm = keyBy(
    associatePropsArray,
    prop => prop,
    prop => ({
      name: prop,
      value: associateData[prop],
      error: validate(prop, associateData[prop]),
      touched: false,
    }),
  );

  return {
    ...state,
    confirmAssociateForm,
    loading: false,
  };
}

function onFindAssociateError(state, { payload }) {
  return {
    ...state,
    errorMessage: payload.data,
    loading: false,
  };
}

function onBeginBackgroundCheckSuccess(state, { payload }) {
  return {
    ...state,
    loading: false,
    associateId: payload.id,
  };
}

function onBeginBackgroundCheckFail(state, { payload }) {
  return {
    ...state,
    loading: false,
    validationErrors: formatErrorMessage(payload),
  };
}

function onClearStore() {
  return {
    ...initState,
  };
}

export default createReducer(initState, {
  [actions.TOUCH]: onTouch,
  [actions.SHOW_MODAL]: onShowModal,
  [actions.CLOSE_MODAL]: onCloseModal,
  [actions.TOGGLE_ASSOCIATE_SELECT]: onToggleAssociateSelect,
  [actions.SET_STEP]: onSetStep,
  [actions.CREATE_FIND_ASSOCIATE_FORM]: onCreateFindAssociateForm,
  [actions.CHANGE_PROPERTY]: onChangeProperty,
  [actions.LOAD_ASSOCIATE_FORM_DATA_SUCCESS]: onLoadAssociateFormDataSuccess,
  [actions.FIND_ASSOCIATE_REQUEST]: onFindAssociateRequest,
  [actions.FIND_ASSOCIATE_SUCCESS]: onFindAssociateSuccess,
  [actions.FIND_ASSOCIATE_ERROR]: onFindAssociateError,
  [actions.BEGIN_BACKGROUND_CHECK_REQUEST]: onBeginBackgroundCheckRequest,
  [actions.BEGIN_BACKGROUND_CHECK_SUCCESS]: onBeginBackgroundCheckSuccess,
  [actions.BEGIN_BACKGROUND_CHECK_FAIL]: onBeginBackgroundCheckFail,
  [actions.CLEAR_STORE]: onClearStore,
});
